import apiClient from 'apiClient';
import { ValidationResponse, ValidationStatuses } from './types';
import { Nullable } from '../../../utils/types';
import { AxiosResponse } from 'axios';
import cachedValidateRequest from './cachedValidateRequest';
import { ProductRepaymentType } from '../../../domain/Product';

interface MortgageTermParams {
  maxTermLength: number;
  maxAgeAtEndOfTerm: number;
}

interface ResponseData extends Omit<ValidationResponse, 'mortgageTermParams'> {}

interface ResponseDataWithParams extends Required<ValidationResponse<MortgageTermParams>> {}

export const buildValidationMessage = (params: MortgageTermParams) => {
  let baseValidationMessage = `The applicant must not be aged more than ${params.maxAgeAtEndOfTerm} at the end of term.`;
  if (params.maxTermLength < 5) {
    return baseValidationMessage;
  }
  return `${baseValidationMessage} The maximum possible term for this applicant is ${params.maxTermLength} years. Please adjust the term if you would like to continue.`;
};

export class MortgageTermByAgeValidationError extends Error {
  params: MortgageTermParams;

  constructor(params: MortgageTermParams) {
    super('MortgageTermByAgeValidationError');
    this.params = params;
  }
}

const isResponseWithParams = (
  response: AxiosResponse<ValidationResponse<MortgageTermParams>>,
): response is AxiosResponse<ResponseDataWithParams> => response.data.status !== ValidationStatuses.OK;

const validate = async (
  productType: string,
  productRepaymentType: Nullable<ProductRepaymentType>,
  applicant1DateOfBirth: string,
  applicant2DateOfBirth: Nullable<string>,
  productTerm: number,
): Promise<ResponseData> => {
  const response = await apiClient.post<ValidationResponse<MortgageTermParams>>(
    '/api/v1/business-rules/validate-mortgage-term-by-age',
    {
      productType,
      applicant1DateOfBirth,
      applicant2DateOfBirth,
      productTerm,
      productRepaymentType,
    },
  );

  if (isResponseWithParams(response)) {
    throw new MortgageTermByAgeValidationError(response.data.params);
  }

  return { status: response.data.status, details: response.data.details };
};

const cachedValidate = async (
  productType: string,
  productRepaymentType: Nullable<ProductRepaymentType>,
  applicant1DateOfBirth: string,
  applicant2DateOfBirth: Nullable<string>,
  productTerm: number,
) => {
  return cachedValidateRequest(async () => {
    return await validate(productType, productRepaymentType, applicant1DateOfBirth, applicant2DateOfBirth, productTerm);
  }, `validate-mortgage-term-by-age-${productType}-${productRepaymentType}-${applicant1DateOfBirth}-${applicant2DateOfBirth}-${productTerm}`);
};

const exportObject = { validate, cachedValidate };

export default exportObject;
